import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';

import { convertToMoney } from '../utils/formats';
import { validateSession } from '../utils/functions';
import { conditionLabel } from '../utils/usedGame';

var config = process.env;

// El historial vive en sessionStorage y no en el estado solamente: el cajero navega a la
// venta y vuelve, y perder los chequeos de la mañana en cada ida y vuelta lo vuelve inútil.
const HISTORY_KEY = 'priceCheckHistory';
const HISTORY_MAX = 50;

// Los precios se consultan al calculatePrice REAL del backend (uno por medio de pago) en vez
// de recalcular acá: cualquier regla nueva de precio (cupón automático, promo) se refleja sola
// y la consulta nunca puede mostrar un número distinto del que después cobra la venta.
const PAYMENT_TYPES = { EFECTIVO: 1, TRANSFERENCIA: 2, POS: 5 };

class PriceCheckPage extends Component {

    constructor() {
        super();
        this.state = {
            items: [],
            suggestions: [],
            // Desambiguador de código de barras (mismo caso que la venta, ENI-202): el usado
            // lleva el mismo EAN impreso que la copia nueva. null = cerrado.
            barcodeMatches: null,
            barcodeScanned: '',
            selected: null,
            prices: null,
            loading: false,
            history: [],
        };
        this.inputRef = React.createRef();
        this.disambiguatorRef = React.createRef();
    }

    async componentDidMount() {
        let role = await validateSession();
        this.setState({ checked: true, role: role, history: this.loadHistory() });
        this.loadProducts();
    }

    loadHistory = () => {
        try {
            const raw = sessionStorage.getItem(HISTORY_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }

    saveHistory = (history) => {
        try {
            sessionStorage.setItem(HISTORY_KEY, JSON.stringify(history));
        } catch (e) {
            // sessionStorage lleno o bloqueado: el historial en pantalla sigue funcionando.
        }
    }

    clearHistory = () => {
        sessionStorage.removeItem(HISTORY_KEY);
        this.setState({ history: [] });
    }

    loadProducts = () => {
        var request = {
            method: 'GET',
            headers: {
                'access-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            }
        };
        fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'product/all', request)
        .then(results => { return results.json(); })
        .then(data => {
            if (data.error > 0) {
                alert(data.message);
                if (data.error == 1002) {
                    window.location.href = localStorage.getItem('homePageURL');
                }
            } else {
                this.setState({ items: data.data }, () => {
                    if (this.inputRef.current) {
                        this.inputRef.current.focus();
                    }
                });
            }
        });
    }

    // ENI-202: el barCode se compara SIEMPRE como string normalizado; con `==` la coerción
    // hace que '007' matchee 7 (el alta int-ifica el valor contra una columna VARCHAR).
    normalizeBarcode = (value) => String(value ?? '').trim();

    debounce = (func, delay) => {
        let timeoutId;
        return function(...args) {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(this, args), delay);
        };
    };

    drawUsedBadge = (product) => (
        Number(product.productType) === 3
            ? <span className="badge badge-warning ml-1">USADO{product.condition ? ` · ${conditionLabel(product.condition)}` : ''}</span>
            : null
    );

    drawNoStockBadge = (product) => (
        Number(product.stock) > 0 ? null : <span className="badge badge-secondary ml-1">SIN STOCK</span>
    );

    clearInput = (refocus = true) => {
        const input = this.inputRef.current;
        if (input) {
            input.value = '';
            if (refocus) {
                input.focus();
            }
        }
    }

    // Un solo input sirve para bipear y para buscar por nombre/código: la pistola resuelve
    // sola (match exacto de barCode) y lo tipeado a mano cae a la lista de sugerencias.
    handleInputChange = this.debounce((event) => {
        const typed = String(event.target.value ?? '');
        const scanned = this.normalizeBarcode(typed);
        if (scanned === '' || scanned === '0') {
            this.setState({ suggestions: [] });
            return;
        }
        // Con el desambiguador abierto se ignora lo nuevo para no pisar la elección pendiente.
        if (this.state.barcodeMatches != null) {
            return;
        }
        // A diferencia de la venta, acá NO se filtra stock 0: consultar el precio de algo
        // agotado es legítimo (¿cuánto sale cuando vuelva?). El desambiguador muestra el stock.
        const matches = this.state.items.filter((element) => {
            const barCode = this.normalizeBarcode(element.barCode);
            return barCode !== '' && barCode !== '0' && barCode === scanned;
        });
        if (matches.length === 1) {
            this.checkPrice(matches[0]);
            this.clearInput();
            this.setState({ suggestions: [] });
            return;
        }
        if (matches.length > 1) {
            // Nuevo primero y con stock primero, para que el orden no dependa del catálogo.
            const sorted = [...matches].sort((a, b) =>
                ((Number(a.productType) === 3) - (Number(b.productType) === 3))
                || ((Number(b.stock) > 0) - (Number(a.stock) > 0))
            );
            this.setState({ barcodeMatches: sorted, barcodeScanned: scanned, suggestions: [] }, () => {
                // Vaciar el input SIN foco y enfocar el modal: Escape y 1..9 llegan, y la
                // pistola no sigue alimentando un campo tapado por el backdrop.
                this.clearInput(false);
                if (this.disambiguatorRef.current) {
                    this.disambiguatorRef.current.focus();
                }
            });
            return;
        }
        // Sin match de código de barras: búsqueda por nombre o código interno. OJO: no limpiar
        // el input acá — la pistola dispara un onChange por carácter y los prefijos todavía
        // no matchean (ENI-202).
        const needle = typed.toLowerCase().trim();
        if (needle.length < 2) {
            this.setState({ suggestions: [] });
            return;
        }
        const suggestions = this.state.items.filter((element) => {
            const code = `${element.prefix}${element.code}`.toLowerCase();
            return element.name.toLowerCase().includes(needle) || code.includes(needle);
        }).slice(0, 8);
        this.setState({ suggestions: suggestions });
    }, 150);

    pickSuggestion = (element) => {
        this.checkPrice(element);
        this.setState({ suggestions: [] }, () => this.clearInput());
    }

    pickBarcodeMatch = (element) => {
        this.checkPrice(element);
        this.setState({ barcodeMatches: null, barcodeScanned: '' }, () => this.clearInput());
    }

    closeBarcodeDisambiguator = () => {
        this.setState({ barcodeMatches: null, barcodeScanned: '' }, () => this.clearInput());
    }

    onDisambiguatorKeyDown = (event) => {
        const matches = this.state.barcodeMatches || [];
        if (event.key === 'Escape') {
            this.closeBarcodeDisambiguator();
            return;
        }
        const index = Number(event.key) - 1;
        if (Number.isInteger(index) && index >= 0 && matches[index]) {
            this.pickBarcodeMatch(matches[index]);
        }
    }

    fetchPriceFor = (product, paymentType) => {
        var request = {
            method: 'POST',
            headers: {
                'access-token': localStorage.getItem('token'),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                code: "",
                products: [{
                    quantity: 1,
                    price: product.price,
                    discount: product.discount,
                    code: product.code
                }],
                paymentType: paymentType,
                // Mostrador = retiro en local: el envío no participa de la consulta.
                shippingType: 2
            })
        };
        return fetch(config.REACT_APP_BASE_URL + config.REACT_APP_VERSION + 'purchase/calculatePrice', request)
            .then(results => { return results.json(); });
    }

    checkPrice = (product) => {
        this.setState({ selected: product, prices: null, loading: true });
        Promise.all([
            this.fetchPriceFor(product, PAYMENT_TYPES.POS),
            this.fetchPriceFor(product, PAYMENT_TYPES.EFECTIVO),
            this.fetchPriceFor(product, PAYMENT_TYPES.TRANSFERENCIA),
        ])
        .then(([pos, efectivo, transferencia]) => {
            const failed = [pos, efectivo, transferencia].find((r) => r.error > 0);
            if (failed) {
                this.setState({ loading: false });
                alert(failed.message);
                if (failed.error == 1002) {
                    window.location.href = localStorage.getItem('homePageURL');
                }
                return;
            }
            const prices = {
                contado: pos.data.price.final_price,
                efectivo: efectivo.data.price.final_price,
                transferencia: transferencia.data.price.final_price,
            };
            const entry = {
                time: new Date().toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' }),
                name: product.name,
                code: `${product.prefix}${product.code}`,
                productType: product.productType,
                condition: product.condition,
                listPrice: product.price,
                discount: product.discount,
                contado: prices.contado,
                efectivo: prices.efectivo,
                transferencia: prices.transferencia,
            };
            const history = [entry, ...this.state.history].slice(0, HISTORY_MAX);
            this.saveHistory(history);
            this.setState({ prices: prices, loading: false, history: history });
        })
        .catch(() => {
            this.setState({ loading: false });
            alert("No se pudo consultar el precio. Probá de nuevo.");
        });
    }

    drawSuggestions = () => {
        if (this.state.suggestions.length === 0) {
            return null;
        }
        return (
            <div className="list-group" style={{ position: 'absolute', zIndex: 100, width: '100%', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                {this.state.suggestions.map((element) => (
                    <button
                        type="button"
                        key={`${element.prefix}${element.code}`}
                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                        onClick={() => this.pickSuggestion(element)}
                    >
                        <span>
                            {element.name} {this.drawUsedBadge(element)} {this.drawNoStockBadge(element)}
                            <small className="text-muted d-block">{element.prefix}{element.code}</small>
                        </span>
                        <strong>$ {convertToMoney(element.price)}</strong>
                    </button>
                ))}
            </div>
        );
    }

    drawBarcodeDisambiguator = () => {
        const matches = this.state.barcodeMatches;
        if (matches == null) {
            return null;
        }
        return (
            <React.Fragment>
                <div className="modal fade show" style={{ display: 'block' }} role="dialog" tabIndex={-1} ref={this.disambiguatorRef} onKeyDown={this.onDisambiguatorKeyDown}>
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <div>
                                    <h4 className="modal-title mb-1">¿Cuál de estos productos?</h4>
                                    <small className="text-muted">
                                        El código {this.state.barcodeScanned} corresponde a {matches.length} productos. Elegí uno.
                                    </small>
                                </div>
                                <button type="button" className="close" onClick={this.closeBarcodeDisambiguator}><span>&times;</span></button>
                            </div>
                            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                <div className="list-group">
                                    {matches.map((element, index) => (
                                        <button
                                            type="button"
                                            key={`${element.prefix}${element.code}`}
                                            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                            autoFocus={index === 0}
                                            onClick={() => this.pickBarcodeMatch(element)}
                                        >
                                            <span>
                                                {index + 1}. {element.name} {this.drawUsedBadge(element)} {this.drawNoStockBadge(element)}
                                                <small className="text-muted d-block">{element.prefix}{element.code} · Stock {element.stock}</small>
                                            </span>
                                            <strong>$ {convertToMoney(element.price)}</strong>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-outline-secondary" onClick={this.closeBarcodeDisambiguator}>Cancelar</button>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="modal-backdrop fade show" onClick={this.closeBarcodeDisambiguator}></div>
            </React.Fragment>
        );
    }

    drawResult = () => {
        const product = this.state.selected;
        if (product == null) {
            return (
                <div className="card card-small mb-4">
                    <div className="card-body p-4" style={{ textAlign: 'center', color: '#5a6169' }}>
                        Bipeá un juego o buscalo por nombre para ver su precio.
                    </div>
                </div>
            );
        }
        if (this.state.loading) {
            return (
                <div className="card card-small mb-4">
                    <div className="card-body p-4" style={{ textAlign: 'center' }}>
                        <div className="min-loader" style={{ display: 'inline-block' }}></div>
                    </div>
                </div>
            );
        }
        const prices = this.state.prices;
        if (prices == null) {
            return null;
        }
        const hasDiscount = Number(product.discount) > 0;
        // Efectivo y transferencia se configuran por separado (un cupón por medio de pago):
        // casi siempre coinciden y se muestran como una sola fila, pero si el dueño los
        // apuntara a cupones distintos, acá se separan solos.
        const cashEqualsTransfer = prices.efectivo === prices.transferencia;
        const savings = prices.contado - prices.efectivo;
        return (
            <div className="card card-small mb-4">
                <div className="card-body p-4">
                    <h4 style={{ marginBottom: '2px' }}>{product.name} {this.drawUsedBadge(product)} {this.drawNoStockBadge(product)}</h4>
                    <small className="text-muted">Código {product.prefix}{product.code}</small>
                    <div className="row" style={{ marginTop: '20px', textAlign: 'center' }}>
                        <div className="col-md-4 mb-3">
                            <div style={{ color: '#5a6169', textTransform: 'uppercase', fontSize: '13px' }}>Precio de lista</div>
                            <div style={{ fontSize: '28px' }}>
                                {hasDiscount
                                    ? <span><s style={{ color: '#868e96' }}>$ {convertToMoney(product.price)}</s></span>
                                    : <strong>$ {convertToMoney(product.price)}</strong>}
                            </div>
                            {hasDiscount && (
                                <div style={{ color: '#c4183c', fontWeight: 'bold' }}>-{product.discount}% de descuento</div>
                            )}
                        </div>
                        <div className="col-md-4 mb-3">
                            <div style={{ color: '#5a6169', textTransform: 'uppercase', fontSize: '13px' }}>Tarjeta / MercadoPago</div>
                            <div style={{ fontSize: '28px' }}><strong>$ {convertToMoney(prices.contado)}</strong></div>
                        </div>
                        <div className="col-md-4 mb-3">
                            <div style={{ color: '#5a6169', textTransform: 'uppercase', fontSize: '13px' }}>
                                {cashEqualsTransfer ? 'Efectivo o transferencia' : 'Efectivo'}
                            </div>
                            <div style={{ fontSize: '28px', color: '#17c671' }}><strong>$ {convertToMoney(prices.efectivo)}</strong></div>
                            {!cashEqualsTransfer && (
                                <div style={{ marginTop: '6px' }}>
                                    <div style={{ color: '#5a6169', textTransform: 'uppercase', fontSize: '13px' }}>Transferencia</div>
                                    <div style={{ fontSize: '28px', color: '#17c671' }}><strong>$ {convertToMoney(prices.transferencia)}</strong></div>
                                </div>
                            )}
                            {savings > 0 && (
                                <div style={{ color: '#17c671' }}>ahorra $ {convertToMoney(savings)}</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    drawHistory = () => {
        const history = this.state.history;
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex justify-content-between align-items-center">
                    <span>Chequeos anteriores</span>
                    {history.length > 0 && (
                        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={this.clearHistory}>Limpiar</button>
                    )}
                </div>
                <div className="card-body p-0">
                    <table className="table mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th style={{ width: '10%' }}>Hora</th>
                                <th style={{ width: '40%' }}>Producto</th>
                                <th style={{ width: '17%', textAlign: 'right' }}>Lista</th>
                                <th style={{ width: '16%', textAlign: 'right' }}>Tarjeta / MP</th>
                                <th style={{ width: '17%', textAlign: 'right' }}>Efectivo / Transf.</th>
                            </tr>
                        </thead>
                        <tbody>
                            {history.length === 0 ? (
                                <tr><td colSpan="5" style={{ textAlign: 'center', color: '#5a6169' }}>Todavía no consultaste ningún precio.</td></tr>
                            ) : history.map((entry, index) => (
                                <tr key={index}>
                                    <td>{entry.time}</td>
                                    <td>
                                        {entry.name} {this.drawUsedBadge(entry)}
                                        <small className="text-muted d-block">{entry.code}</small>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        {Number(entry.discount) > 0
                                            ? <span><s className="text-muted">$ {convertToMoney(entry.listPrice)}</s> <small className="text-danger">-{entry.discount}%</small></span>
                                            : <span>$ {convertToMoney(entry.listPrice)}</span>}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>$ {convertToMoney(entry.contado)}</td>
                                    <td style={{ textAlign: 'right', color: '#17c671' }}>
                                        $ {convertToMoney(entry.efectivo)}
                                        {entry.efectivo !== entry.transferencia && (
                                            <small className="d-block">Transf.: $ {convertToMoney(entry.transferencia)}</small>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }
        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={38} role={this.state.role}/>
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4" style={{ paddingBottom: '40px' }}>
                            <HeaderComponent title="Venta" subtitle="Consulta de precio"/>
                            <div className="card card-small mb-4">
                                <div className="card-body p-3">
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            autoComplete="off"
                                            className="form-control form-control-lg"
                                            type="text"
                                            placeholder="Bipeá el código de barras o buscá por nombre"
                                            ref={this.inputRef}
                                            onChange={(e) => this.handleInputChange(e)}
                                        />
                                        {this.drawSuggestions()}
                                    </div>
                                </div>
                            </div>
                            {this.drawResult()}
                            {this.drawHistory()}
                        </div>
                    </main>
                </div>
                {this.drawBarcodeDisambiguator()}
            </div>
        );
    }
}

export default PriceCheckPage;
